import { memo } from 'react';
import { resolveMessageMedia, type ChatMessage } from '@teo/widget-shared';
import { AudioPlayer } from './AudioPlayer.js';
import { FileAttachment } from './FileAttachment.js';
import { ImageAttachment } from './ImageAttachment.js';
import { LinkPreviewCard } from './LinkPreviewCard.js';
import { VideoAttachment } from './VideoAttachment.js';
import { VideoEmbedPreview } from './VideoEmbedPreview.js';
import { MessageText } from '../components/MessageText.js';

type RichMessageContentProps = {
  message: ChatMessage;
  onMediaLoad?: () => void;
};

function MessageCaption({ text }: { text: string }) {
  return <MessageText text={text} className="teo-rich-media__caption" />;
}

export const RichMessageContent = memo(function RichMessageContent({
  message,
  onMediaLoad,
}: RichMessageContentProps) {
  const { attachment, linkPreviews, caption } = resolveMessageMedia(message);
  const hasRichContent =
    Boolean(attachment) ||
    linkPreviews.length > 0 ||
    Boolean(caption?.trim());

  if (!hasRichContent) {
    return <MessageText text={message.text} />;
  }

  return (
    <div className="teo-rich-media">
      {attachment?.kind === 'image' && (
        <ImageAttachment attachment={attachment} onMediaLoad={onMediaLoad} />
      )}
      {attachment?.kind === 'video' && (
        <VideoAttachment attachment={attachment} onMediaLoad={onMediaLoad} />
      )}
      {attachment?.kind === 'video_embed' && (
        <VideoEmbedPreview
          attachment={attachment}
          onMediaLoad={onMediaLoad}
        />
      )}
      {attachment?.kind === 'audio' && (
        <AudioPlayer attachment={attachment} />
      )}
      {attachment?.kind === 'file' && (
        <FileAttachment attachment={attachment} />
      )}

      {caption && <MessageCaption text={caption} />}

      {linkPreviews.length > 0 && (
        <div className="teo-rich-media__previews">
          {linkPreviews.map((preview) => (
            <LinkPreviewCard
              key={preview.url}
              preview={preview}
              onMediaLoad={onMediaLoad}
            />
          ))}
        </div>
      )}
    </div>
  );
}, richMessageContentPropsEqual);

function richMessageContentPropsEqual(
  prev: RichMessageContentProps,
  next: RichMessageContentProps,
): boolean {
  if (prev.onMediaLoad !== next.onMediaLoad) return false;
  if (prev.message === next.message) return true;

  const prevMedia = resolveMessageMedia(prev.message);
  const nextMedia = resolveMessageMedia(next.message);

  if (prevMedia.caption !== nextMedia.caption) return false;
  if (prevMedia.attachment !== nextMedia.attachment) return false;
  if (prevMedia.linkPreviews.length !== nextMedia.linkPreviews.length) {
    return false;
  }
  for (let index = 0; index < prevMedia.linkPreviews.length; index += 1) {
    if (prevMedia.linkPreviews[index]?.url !== nextMedia.linkPreviews[index]?.url) {
      return false;
    }
  }

  return prev.message.text === next.message.text;
}
